import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import {
  NextStep,
  PreviousStep,
  SkipToStep,
  UpdateTooltipMessage,
  UpdateMonetizationStep,
  UpdatePlanStep,
} from './stepper.actions';

export class StepperStateModel {
  currentStep: number;
  steps: Step[];
  tooltipMessage: string;
}

export interface Step {
  translationKey: string;
  status: 'current' | 'disabled' | 'done';
  getUrl: (appId: number) => string;
}

const initialSteps: Step[] = [
  {
    translationKey: marker('stepper.name'),
    status: 'current',
    getUrl: (appId: number) => `/create/${appId}`,
  },
  {
    translationKey: marker('stepper.design'),
    status: 'disabled',
    getUrl: (appId: number) => `/configure/${appId}`,
  },
  {
    translationKey: marker('stepper.plan'),
    status: 'disabled',
    getUrl: (appId: number) => `/plan/${appId}`,
  },
  {
    translationKey: marker('stepper.images'),
    status: 'disabled',
    getUrl: (appId: number) => `/assets/${appId}`,
  },
  {
    translationKey: marker('stepper.monetization'),
    status: 'disabled',
    getUrl: (appId: number) => `/monetization/${appId}`,
  },
  {
    translationKey: marker('stepper.last'),
    status: 'disabled',
    getUrl: (appId: number) => `/publishing/${appId}`,
  },
];

@State<StepperStateModel>({
  name: 'stepper',
  defaults: {
    currentStep: 0,
    steps: initialSteps,
    tooltipMessage: null,
  },
})
@Injectable()
export class StepperState implements OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly store: Store,
  ) {
    // remove plan step for paid apps
    this.subscriptions.add(this.store
      .select(AppConfigurationState.isPaidApp)
      .subscribe(isPaidApp => {
        if (isPaidApp !== undefined) {
          this.store.dispatch(new UpdatePlanStep(isPaidApp));
        }
      }))

    // remove monetization step for non monetized planss
    this.subscriptions.add(this.store
      .select(AppConfigurationState.monetizationAvailable)
      .subscribe(isAvailable => {
        if (isAvailable !== undefined) {
          this.store.dispatch(new UpdateMonetizationStep(isAvailable));
        }
      }))
  }

  @Selector()
  static currentStep(state: StepperStateModel): number {
    return state.currentStep;
  }

  @Selector()
  static steps(state: StepperStateModel): Step[] {
    return state.steps;
  }

  @Selector()
  static stepsLength(state: StepperStateModel): number {
    return state.steps.length;
  }

  @Selector()
  static isLastStep(state: StepperStateModel): boolean {
    return state.currentStep >= state.steps.length - 1;
  }

  @Selector()
  static tooltipMessage(state: StepperStateModel): string {
    return state.tooltipMessage;
  }

  @Action(NextStep)
  nextStep(ctx: StateContext<StepperStateModel>) {
    const state = ctx.getState();
    if (state.currentStep < state.steps.length) {
      const newCurrentStep = state.currentStep + 1;
      const updatedSteps = this.updateStepStatuses(state.steps, newCurrentStep);

      ctx.patchState({
        currentStep: newCurrentStep,
        steps: updatedSteps,
      });
      this.navigateToStep(ctx);
    }
  }

  @Action(PreviousStep)
  previousStep(ctx: StateContext<StepperStateModel>) {
    const state = ctx.getState();
    if (state.currentStep > 0) {
      const newCurrentStep = state.currentStep - 1;
      const updatedSteps = this.updateStepStatuses(state.steps, newCurrentStep);

      ctx.patchState({
        currentStep: newCurrentStep,
        steps: updatedSteps,
      });
      this.navigateToStep(ctx);
    }
  }

  @Action(SkipToStep)
  skipToStep(ctx: StateContext<StepperStateModel>, action: SkipToStep) {
    const state = ctx.getState();
    const targetStep = action.targetStep;

    if (targetStep >= 0 && targetStep < state.steps.length) {
      const updatedSteps = this.updateStepStatuses(state.steps, targetStep);
      ctx.patchState({
        currentStep: targetStep,
        steps: updatedSteps,
      });
    }
  }

  @Action(UpdateTooltipMessage)
  updateTooltipMessage(
    ctx: StateContext<StepperStateModel>,
    { tooltipMessage }: UpdateTooltipMessage,
  ) {
    ctx.patchState({ tooltipMessage });
  }

  @Action(UpdateMonetizationStep)
  updateMonetizationStep(
    ctx: StateContext<StepperStateModel>,
    { shouldInclude }: UpdateMonetizationStep,
  ) {
    const state = ctx.getState();
    const currentSteps = state.steps;
    const hasMonetization = currentSteps.some(
      s => s.translationKey === marker('stepper.monetization'),
    );

    // early return if no change needed
    if (shouldInclude === hasMonetization) {
      return;
    }

    let updatedSteps: Step[];

    if (shouldInclude) {
      // add monetization step before the last step
      const monetizationTemplate = initialSteps.find(
        s => s.translationKey === marker('stepper.monetization'),
      );
      const newStep: Step = monetizationTemplate
        ? { ...monetizationTemplate, status: 'disabled' }
        : {
          translationKey: marker('stepper.monetization'),
          status: 'disabled',
          getUrl: (appId: number) => `/monetization/${appId}`,
        };

      const lastIndex = currentSteps.findIndex(
        s => s.translationKey === marker('stepper.last'),
      );
      updatedSteps =
        lastIndex >= 0
          ? [
            ...currentSteps.slice(0, lastIndex),
            newStep,
            ...currentSteps.slice(lastIndex),
          ]
          : [...currentSteps, newStep];
    } else {
      // remove monetization step
      updatedSteps = currentSteps.filter(
        step => step.translationKey !== marker('stepper.monetization'),
      );
    }

    ctx.patchState({ steps: updatedSteps });
  }

  @Action(UpdatePlanStep)
  updatePlanStep(
    ctx: StateContext<StepperStateModel>,
    { isPaidApp }: UpdatePlanStep,
  ) {
    // for paid apps, always rebuild from initialSteps without plan step
    // for non-paid apps, use initialSteps as-is
    const updatedSteps = isPaidApp
      ? initialSteps.filter(
        step => step.translationKey !== marker('stepper.plan'),
      )
      : initialSteps;

    ctx.patchState({ steps: updatedSteps });
  }

  private updateStepStatuses(steps: Step[], newCurrentStep: number): Step[] {
    return steps.map((step, index) => {
      if (index < newCurrentStep) {
        return { ...step, status: 'done' };
      } else if (index === newCurrentStep) {
        return { ...step, status: 'current' };
      } else {
        return { ...step, status: 'disabled' };
      }
    });
  }

  private navigateToStep(ctx: StateContext<StepperStateModel>) {
    const steps = ctx.getState().steps;
    const step = steps[ctx.getState().currentStep];
    if (step && step.getUrl) {
      const appId = this.store.selectSnapshot(AppConfigurationState.currentAppConfigId);
      const url = step.getUrl(appId);
      this.router.navigateByUrl(url);
    }
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
